import { useEffect, useState } from 'react';

type Listener<T> = (state: T) => void;
type SetStateArg<T> = (T extends (...args: any[]) => any ? never : T) | ((currentState: T) => T);

export interface SimpleStore<T> {
  readonly subscribe: (listener: Listener<T>) => void;
  readonly unsubscribe: (listener: Listener<T>) => void;
  readonly getStore: () => T;
  readonly setStore: (arg: SetStateArg<T>) => T;
}

export const createSimpleStore = <T extends object>(initialStore: T): SimpleStore<T> => {
  let currentStore = initialStore;
  const listeners = new Set<Listener<T>>();

  return {
    subscribe: listener => {
      listeners.add(listener);
    },
    unsubscribe: listener => {
      listeners.delete(listener);
    },
    getStore: () => currentStore,
    setStore: (arg: SetStateArg<T>) => {
      if (typeof arg === 'function') {
        currentStore = arg(currentStore);
      } else {
        currentStore = arg;
      }

      listeners.forEach(listener => listener(currentStore));
      return currentStore;
    }
  };
};

function useSimpleStore<T extends object>(
  store: SimpleStore<T>,
  selector?: undefined,
  eqFn?: undefined
): [T, SimpleStore<T>['setStore']];

function useSimpleStore<T extends object, U = T>(
  store: SimpleStore<T>,
  selector: (store: T) => U,
  eqFn?: (prev: U, next: U) => boolean
): [U, SimpleStore<T>['setStore']];

// A hook to "connect" local state to a shared store
function useSimpleStore<T extends object, U = T>(
  store: SimpleStore<T>,
  selector: (store: T) => U | T = val => val,
  eqFn: (prev: U | T, next: U | T) => boolean = (prev, next) => {
    if (!Array.isArray(prev) || !Array.isArray(next)) return prev === next;

    if (prev.length !== next.length) return false;

    return prev.every((value, i) => value === next[i]);
  }
) {
  const [state, setState] = useState(() => selector(store.getStore()));

  useEffect(() => {
    const listener: Listener<T> = newState => {
      setState(previousState => {
        const nextState = selector(newState);

        return eqFn(previousState, nextState) ? previousState : nextState;
      });
    };

    store.subscribe(listener);

    return () => {
      store.unsubscribe(listener);
    };
  }, []);

  return [state, store.setStore] as const;
}

export default useSimpleStore;
